import type { Survey, SurveyStatus } from '@sse/schema';
import { ClipboardList, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { btnPrimary } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

export const SURVEY_STATUS: Record<SurveyStatus, [string, string]> = {
  draft: ['Bozza', 'bg-surface-strong text-fg'],
  active: ['Sul sito', 'bg-ok-soft text-fg'],
  closed: ['Chiuso', 'bg-surface text-muted'],
};

export function SurveyBadge({ status }: { status: SurveyStatus }) {
  const [label, cls] = SURVEY_STATUS[status];
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${cls}`}
    >
      {label}
    </span>
  );
}

/**
 * Sondaggi (solo admin, docs/07-admin-e-bot.md "Sondaggi"): sul sito compaiono solo quelli
 * pubblicati, con una barra in cima a ogni pagina e un riquadro in home. Senza sondaggi
 * pubblicati il sito non mostra nulla.
 */
export function SurveysList() {
  const { api, isAdmin } = useSession();
  const [rows, setRows] = useState<Survey[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    api<{ surveys: Survey[] }>('/surveys')
      .then((r) => setRows(r.surveys))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [api, isAdmin]);

  if (!isAdmin)
    return <main className="p-6 text-muted">I sondaggi sono riservati agli admin.</main>;

  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Sondaggi</h1>
        <Link to="/surveys/new" className={btnPrimary}>
          <Plus className="size-4" aria-hidden /> Nuovo sondaggio
        </Link>
      </div>
      <p className="mt-1 max-w-[70ch] text-sm text-muted">
        Un sondaggio pubblicato compare sul sito in una barra in cima a ogni pagina e in un riquadro
        in home, fino alla data di chiusura. Chi risponde conferma l’email con un codice: una
        risposta per persona, e l’email non viene salvata con le risposte.
      </p>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {!rows ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : rows.length === 0 ? (
        <div className="mt-6 flex flex-col items-start gap-3 border border-surface-strong p-6">
          <ClipboardList className="size-8 text-muted" aria-hidden />
          <p>Nessun sondaggio: sul sito non compare nulla finché non ne pubblichi uno.</p>
        </div>
      ) : (
        <ul className="mt-6 flex flex-col divide-y divide-surface-strong border-y border-surface-strong">
          {rows.map((s) => (
            <li key={s.id}>
              <Link
                to={`/surveys/${s.id}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-3 hover:bg-surface"
              >
                <span className="min-w-0 flex-1 font-medium">{s.title}</span>
                <SurveyBadge status={s.status} />
                <span className="text-sm text-muted">
                  {s.responses} {s.responses === 1 ? 'risposta' : 'risposte'} · {s.questions.length}{' '}
                  {s.questions.length === 1 ? 'domanda' : 'domande'}
                  {s.endsOn ? ` · fino al ${s.endsOn.split('-').reverse().join('/')}` : ''} · creato{' '}
                  {formatStamp(s.createdAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
